import { Alert, AlertDescription } from "@cleanbudget/ui/components/alert";
import { IconAlertCircle } from "@tabler/icons-react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { BackLink } from "@/components/back-link";
import { moveId, scrollToForm } from "@/components/order-buttons";
import { PageHeader } from "@/components/page-header";
import { AttributeCard } from "./-components/attribute-card";
import { AttributeForm } from "./-components/attribute-form";
import { attributesQuery, useAttributeErrorMessage, useAttributeWrites } from "./-components/use-attributes";

/**
 * Producten → Kenmerken (#160): what the shop filters on, each attribute a card with its values.
 * Attributes and values move with their arrows; one attribute can be marked as the size (its value
 * leads the size label). Deleting a value or an attribute takes it off every product and variant.
 */
export const Route = createFileRoute("/_admin/product/attribute/")({
	loader: ({ context }) => context.queryClient.ensureQueryData(attributesQuery()),
	component: AttributesPage,
});

function AttributesPage() {
	const { t } = useTranslation("attribute");
	const { t: tProduct } = useTranslation("product");
	const { data: attributes } = useSuspenseQuery(attributesQuery());
	const writes = useAttributeWrites();
	const errorMessage = useAttributeErrorMessage();
	const [editingId, setEditingId] = useState<string>();
	const [problem, setProblem] = useState<string>();
	const [announcement, setAnnouncement] = useState("");
	const editing = attributes.find((attribute) => attribute.id === editingId);

	return (
		<div className="flex max-w-3xl flex-col gap-6">
			<PageHeader
				back={<BackLink to="/product">{tProduct("title")}</BackLink>}
				title={t("title")}
				description={t("description")}
				help="attribute:attributes"
			/>
			{problem ? (
				<Alert variant="destructive">
					<IconAlertCircle aria-hidden />
					<AlertDescription>{problem}</AlertDescription>
				</Alert>
			) : null}
			{attributes.length === 0 ? (
				<p className="rounded-2xl bg-card p-10 text-center text-muted-foreground ring-1 ring-foreground/10">
					{t("empty")}
				</p>
			) : (
				attributes.map((attribute, index) => (
					<AttributeCard
						key={attribute.id}
						attribute={attribute}
						index={index}
						count={attributes.length}
						writes={writes}
						onEdit={() => {
							setEditingId(attribute.id);
							scrollToForm("attribute-form");
						}}
						onDeleted={() => (editingId === attribute.id ? setEditingId(undefined) : undefined)}
						onMoved={(to) => {
							setProblem(undefined);
							writes.reorder.mutate(
								{
									ids: moveId(
										attributes.map((entry) => entry.id),
										index,
										to,
									),
								},
								{ onError: (error) => setProblem(errorMessage(error)) },
							);
							setAnnouncement(
								t("moved", { name: attribute.name, position: to + 1, total: attributes.length }),
							);
						}}
					/>
				))
			)}
			<div id="attribute-form" className="scroll-mt-4">
				{editing ? (
					<AttributeForm
						key={editing.id}
						attribute={editing}
						writes={writes}
						onDone={() => setEditingId(undefined)}
					/>
				) : (
					<AttributeForm writes={writes} />
				)}
			</div>
			<p aria-live="polite" className="sr-only">
				{announcement}
			</p>
		</div>
	);
}
